import useGame from '@/hooks/useGame'
import { useMemo } from 'react'
import useAuth from '../useAuth'
import type { Permission } from './permissions'
import {
  hasAllPermissions as hasAllPermissionsUtil,
  hasAnyPermission as hasAnyPermissionUtil,
  hasGameAccess as hasGameAccessUtil,
  hasPermission,
  isGameAvailable as isGameAvailableUtil,
} from './permissions'

export function useHasPermission(
  permission: Permission,
  gameId?: string
): boolean {
  const { user } = useAuth()
  const game = useGame()

  const targetGameId = gameId || game?.id

  return useMemo(
    () => hasPermission(user, permission, targetGameId),
    [user, permission, targetGameId]
  )
}

export function useHasAnyPermission(
  permissions: Permission[],
  gameId?: string
): boolean {
  const { user } = useAuth()
  const game = useGame()

  const targetGameId = gameId || game?.id

  return useMemo(
    () => hasAnyPermissionUtil(user, permissions, targetGameId),
    [user, permissions, targetGameId]
  )
}

export function useHasAllPermissions(
  permissions: Permission[],
  gameId?: string
): boolean {
  const { user } = useAuth()
  const game = useGame()

  const targetGameId = gameId || game?.id

  return useMemo(
    () => hasAllPermissionsUtil(user, permissions, targetGameId),
    [user, permissions, targetGameId]
  )
}

export function useGameAccess(gameId?: string): boolean {
  const { user } = useAuth()
  const game = useGame()

  const targetGameId = gameId || game?.id

  if (targetGameId) {
    return useMemo(
      () => isGameAvailableUtil(user, targetGameId),
      [user, targetGameId]
    )
  }

  return useMemo(() => hasGameAccessUtil(user), [user])
}

export function useIsSuperAdmin(): boolean {
  const { user } = useAuth()
  return useMemo(() => !!user?.isSuperAdmin, [user])
}

export function useHasGlobalModulesAccess(): boolean {
  const { user } = useAuth()
  return useMemo(
    () => !!user?.isSuperAdmin || !!user?.globalModulesAccess,
    [user]
  )
}

export function useHasAdminManagementAccess(): boolean {
  const { user } = useAuth()
  return useMemo(
    () => !!user?.isSuperAdmin || !!user?.adminManagement,
    [user]
  )
}
